import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { updateProfile } from '@firebase/auth';
import { auth } from '../firebase';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import OnlineDot, { PeerStatus } from '../components/OnlineDot';
import FriendsList from '../components/FriendsList';
import MediaBox from '../components/MediaBox';
import PostMediaGallery, { hasMultiMedia } from '../components/PostMediaGallery';
import VerifiedBadge from '../components/VerifiedBadge';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { compressImage } from '../utils/imageCompression';
import { primeMediaUrl } from '../utils/mediaApi';
import { describeModerationError } from '../utils/moderationErrors';
import { renderTextWithLinks } from '../utils/text';
import {
  acceptFriendRequest,
  declineFriendRequest,
  deletePost,
  FriendStatus,
  getFriendStatus,
  removeFriend,
  sendFriendRequest,
  followUser,
  getSettings,
  getUserById,
  getUserByUsername,
  getUserMe,
  listFriends,
  listLiveStreams,
  listMyLiveStreams,
  deleteLiveStream,
  listPosts,
  listPulse,
  PostDto,
  PulseDto,
  unfollowUser,
  updateSettings,
  updateUserProfile,
  UserProfileDto,
  waitForAuthUser,
} from '../utils/secureFunctions';
import { appConfirm } from '../utils/dialog';
import { getProfileUrl, getUsernameProfileUrl } from '../utils/urls';

type Tab = 'posts' | 'pulse' | 'live' | 'friends' | 'about';

type EditForm = {
  name: string;
  username: string;
  bio: string;
  location: string;
  website: string;
  interests: string;
  dateOfBirth: string;
  gender: string;
  showLocation: boolean;
  showAge: boolean;
  showGender: boolean;
};

const formFromProfile = (p: UserProfileDto): EditForm => {
  const privacy = (p.profilePrivacy || {}) as Record<string, unknown>;
  return {
    name: p.name || '',
    username: p.username || '',
    bio: p.bio || '',
    location: p.location || '',
    website: p.website || '',
    interests: (p.interests || []).join(', '),
    dateOfBirth: p.dateOfBirth || '',
    gender: p.gender || '',
    showLocation: privacy.showLocation !== false,
    showAge: privacy.showAge === true,
    showGender: privacy.showGender === true,
  };
};

const friendlyError = (err: unknown, fallback: string) => {
  const moderation = describeModerationError(err);
  if (moderation) return moderation;
  const msg = String((err as any)?.message || '');
  if (msg.includes('USERNAME_TAKEN')) return 'That username is already taken.';
  if (msg.includes('INVALID_USERNAME')) return 'Usernames may only contain letters, numbers, dots, dashes and underscores.';
  if (msg.includes('USER_BLOCKED')) return 'You cannot interact with this user.';
  if (msg.includes('TOO_MANY_PENDING_REQUESTS')) return 'You have too many pending friend requests. Wait for some to be accepted first.';
  if (msg.includes('REQUEST_NOT_FOUND')) return 'This friend request is no longer available.';
  return msg || fallback;
};

const Profile: React.FC = () => {
  const { userId, username } = useParams<{ userId?: string; username?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [profile, setProfile] = useState<UserProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [posts, setPosts] = useState<PostDto[]>([]);
  const [pulses, setPulses] = useState<PulseDto[]>([]);
  const [streams, setStreams] = useState<any[]>([]);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (searchParams.get('tab') === 'friends' ? 'friends' : 'posts'));
  const [friendCount, setFriendCount] = useState<number | null>(null);
  const tabParam = searchParams.get('tab');
  useEffect(() => { if (tabParam === 'friends') setTab('friends'); }, [tabParam]);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<'photoURL' | 'coverPhotoURL' | null>(null);
  const [uploadPct, setUploadPct] = useState(0);

  const myUid = auth.currentUser?.uid || '';
  const isSelf = !!profile && profile.uid === myUid;
  const [isStaff, setIsStaff] = useState(false);
  const [isAdminViewer, setIsAdminViewer] = useState(false);
  const [friend, setFriend] = useState<{ status: FriendStatus; requestId: string | null }>({ status: 'none', requestId: null });
  const [friendBusy, setFriendBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getUserMe()
      .then((me) => {
        const role = String((me.profile as { role?: string } | undefined)?.role || '').toLowerCase();
        if (!active) return;
        setIsStaff(role === 'admin' || role === 'super_admin' || role === 'moderator');
        setIsAdminViewer(role === 'admin' || role === 'super_admin');
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const profileUid = profile?.uid || '';
  useEffect(() => {
    if (profile && !isSelf) setTab((t) => (t === 'friends' ? 'posts' : t));
  }, [profile, isSelf]);
  useEffect(() => {
    if (!isSelf) return;
    let active = true;
    listFriends().then((r) => {
      if (active) setFriendCount((r.friends || []).length);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [isSelf]);
  useEffect(() => {
    if (!profileUid || isSelf) return;
    let active = true;
    getFriendStatus(profileUid)
      .then((r) => { if (active) setFriend({ status: r.status, requestId: r.requestId }); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [profileUid, isSelf]);

  const runFriendAction = async (action: () => Promise<{ status: FriendStatus }>, confirmText?: string) => {
    if (friendBusy) return;
    if (confirmText && !await appConfirm(confirmText)) return;
    setFriendBusy(true);
    try {
      await action();
      const r = await getFriendStatus(profileUid);
      setFriend({ status: r.status, requestId: r.requestId });
    } catch (err) {
      alert(friendlyError(err, 'Could not update friend request. Please try again.'));
    } finally {
      setFriendBusy(false);
    }
  };

  const handleMessage = () => {
    if (!profile) return;
    if (friend.status !== 'friends' && !isAdminViewer) {
      alert(friend.status === 'incoming'
        ? `Accept ${profile.name}'s friend request first, then you can message each other.`
        : `You need to be friends with ${profile.name} to send a message. Send a friend request first.`);
      return;
    }
    navigate(`/messages?user=${encodeURIComponent(profile.uid)}`);
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadContent = useCallback(async (uid: string, self: boolean) => {
    const [postsRes, pulseRes, liveRes] = await Promise.all([
      listPosts({ limit: 100, userId: uid }).catch(() => ({ posts: [] as PostDto[] })),
      listPulse({ limit: 100, userId: uid }).catch(() => ({ pulses: [] as PulseDto[] })),
      (self ? listMyLiveStreams() : listLiveStreams()).catch(() => ({ streams: [] as any[] })),
    ]);
    setPosts((postsRes.posts || []).filter((x) => x.userId === uid));
    setPulses((pulseRes.pulses || []).filter((x) => x.userId === uid));
    setStreams((liveRes.streams || []).filter((x: any) => x.userId === uid));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      setError(null);
      try {
        const res = username
          ? await getUserByUsername(username.startsWith('@') ? username.slice(1) : username)
          : await getUserById(userId || (await waitForAuthUser())?.uid || '');
        if (cancelled) return;
        const p = res.profile;
        const usernameUrl = getUsernameProfileUrl(p.username);
        const currentLocation = locationRef.current;
        if (usernameUrl && currentLocation.pathname !== usernameUrl) {
          navigate({ pathname: usernameUrl, search: currentLocation.search, hash: currentLocation.hash }, { replace: true });
          return;
        }
        setProfile(p);
        setFollowing(!!p.followedByMe);
        const self = p.uid === auth.currentUser?.uid;
        if (!self) {
          getSettings()
            .then((s) => { if (!cancelled) setBlocked((s.settings.blockedUsers || []).includes(p.uid)); })
            .catch(() => undefined);
        }
        await loadContent(p.uid, self);
      } catch (err) {
        console.error('profile-load-failed', err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, username, loadContent, navigate]);

  const handleFollow = async () => {
    if (!profile || followBusy) return;
    setFollowBusy(true);
    try {
      if (following) {
        await unfollowUser(profile.uid);
        setFollowing(false);
        setProfile((prev) => (prev ? { ...prev, followers: Math.max(0, (prev.followers || 0) - 1) } : prev));
      } else {
        await followUser(profile.uid);
        setFollowing(true);
        setProfile((prev) => (prev ? { ...prev, followers: (prev.followers || 0) + 1 } : prev));
      }
    } catch (err) {
      alert(friendlyError(err, 'Could not update follow status. Please try again.'));
    } finally {
      setFollowBusy(false);
    }
  };

  const handleBlockToggle = async () => {
    if (!profile) return;
    const next = !blocked;
    if (next && !await appConfirm(`Block ${profile.name}? They won't be able to see your content or message you.`)) return;
    try {
      const current = await getSettings();
      const list = new Set(current.settings.blockedUsers || []);
      if (next) list.add(profile.uid); else list.delete(profile.uid);
      await updateSettings({ blockedUsers: Array.from(list) });
      setBlocked(next);
      if (next && following) {
        await unfollowUser(profile.uid).catch(() => undefined);
        setFollowing(false);
      }
    } catch (err) {
      alert(friendlyError(err, 'Could not update block list.'));
    }
  };

  const handleMediaChange = async (field: 'photoURL' | 'coverPhotoURL', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !profile) return;
    if (!file.type.startsWith('image/')) { alert('Please choose an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Image must be smaller than 10 MB.'); return; }
    setUploading(field);
    setUploadPct(0);
    const previewUrl = URL.createObjectURL(file);
    const previous = profile[field];
    // Show the chosen photo immediately while it uploads and is safety-checked.
    setProfile((p) => (p ? { ...p, [field]: previewUrl } : p));
    let committed = false;
    try {
      const small = await compressImage(file, field === 'photoURL'
        ? { maxWidth: 640, maxHeight: 640, quality: 0.85 }
        : { maxWidth: 1920, maxHeight: 1080, quality: 0.82 });
      const upload = await uploadModeratedMedia({
        folder: field === 'photoURL' ? 'profilePhotos' : 'coverPhotos',
        field,
        file: small,
        contentType: small.type,
        onProgress: setUploadPct,
      });
      primeMediaUrl(upload.objectKey, previewUrl);
      const res = await updateUserProfile({ [field]: upload.objectKey, moderationTokens: { [field]: upload.proof } });
      committed = true;
      setProfile(res.profile);
    } catch (err) {
      setProfile((p) => (p ? { ...p, [field]: previous } : p));
      alert(friendlyError(err, 'Upload failed. Please try again.'));
    } finally {
      if (!committed) URL.revokeObjectURL(previewUrl);
      setUploading(null);
    }
  };

  const openEditor = () => {
    if (!profile) return;
    setForm(formFromProfile(profile));
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!form || !profile) return;
    const name = form.name.trim();
    if (!name) { setError('Name is required.'); return; }
    const uname = form.username.trim().replace(/^@/, '');
    if (uname && !/^[A-Za-z0-9._-]{2,40}$/.test(uname)) {
      setError('Username must be 2–40 characters: letters, numbers, dots, dashes or underscores.');
      return;
    }
    const website = form.website.trim();
    if (website && !/^https?:\/\//i.test(website) && !/^[\w.-]+\.[a-z]{2,}/i.test(website)) {
      setError('Please enter a valid website address.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Partial<UserProfileDto> = {
        name,
        bio: form.bio.trim() || null,
        location: form.location.trim() || null,
        website: website || null,
        interests: form.interests.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 20),
        dateOfBirth: form.dateOfBirth || null,
        gender: form.gender || null,
        profilePrivacy: { showLocation: form.showLocation, showAge: form.showAge, showGender: form.showGender },
      };
      if ((uname || null) !== (profile.username || null)) payload.username = uname || null;
      const res = await updateUserProfile(payload);
      setProfile(res.profile);
      if (auth.currentUser && auth.currentUser.displayName !== name) {
        await updateProfile(auth.currentUser, { displayName: name }).catch(() => undefined);
      }
      setEditing(false);
      if (res.profile.username || username) {
        const profileUrl = getUsernameProfileUrl(res.profile.username) || getProfileUrl({ uid: res.profile.uid });
        if (location.pathname !== profileUrl) {
          navigate({ pathname: profileUrl, search: location.search, hash: location.hash }, { replace: true });
        }
      }
    } catch (err) {
      setError(friendlyError(err, 'Failed to save profile.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (id: string) => {
    if (!await appConfirm('Delete this post?')) return;
    try {
      await deletePost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      alert(friendlyError(err, 'Failed to delete post.'));
    }
  };

  const handleDeleteStream = async (id: string) => {
    if (!await appConfirm('Delete this live video? This cannot be undone.')) return;
    try {
      await deleteLiveStream(id);
      setStreams((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert(friendlyError(err, 'Failed to delete live video.'));
    }
  };

  const layoutStyle: React.CSSProperties = isMobile ? styles.contentMobile : styles.content;

  if (loading || notFound || !profile) {
    return (
      <div style={styles.container}>
        <TopNavigation />
        {!isMobile && <LeftNavSidebar />}
        {!isMobile && <RightWidgetsSidebar />}
        <div style={layoutStyle}>
          <div style={styles.card}>
            {notFound ? (
              <>
                <h2 style={{ marginTop: 0 }}>Profile not found</h2>
                <p style={styles.muted}>This account may have been removed or the link is incorrect.</p>
                <button style={styles.primaryBtn} onClick={() => navigate('/home')}>Back to Home</button>
              </>
            ) : (
              <p style={styles.muted}>Loading profile…</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const privacy = (profile.profilePrivacy || {}) as Record<string, unknown>;
  const age = (() => {
    if (!profile.dateOfBirth) return null;
    const dob = new Date(profile.dateOfBirth);
    if (Number.isNaN(dob.getTime())) return null;
    const diff = Date.now() - dob.getTime();
    return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  })();
  const websiteHref = profile.website ? (/^https?:\/\//i.test(profile.website) ? profile.website : `https://${profile.website}`) : '';
  const connectionsBase = `/connections/${encodeURIComponent(profile.uid)}`;

  return (
    <div style={styles.container}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
      <div style={layoutStyle}>
        <div style={styles.headerCard}>
          <div style={styles.cover}>
            {profile.coverPhotoURL ? (
              <ImageWithFallback src={profile.coverPhotoURL} alt="Cover" style={styles.coverImg} />
            ) : null}
            {isSelf && (
              <label style={styles.coverBtn}>
                {uploading === 'coverPhotoURL' ? `Uploading ${uploadPct}%` : '📷 Change cover'}
                <input type="file" accept="image/*" hidden disabled={!!uploading} onChange={(e) => handleMediaChange('coverPhotoURL', e)} />
              </label>
            )}
          </div>
          <div style={styles.headerBody}>
            <div style={styles.avatarWrap}>
              <ImageWithFallback src={profile.photoURL || ''} alt={profile.name} style={styles.avatar} />
              {!isSelf && <OnlineDot userId={profile.uid} size={22} style={{ right: 8, bottom: 8, borderWidth: 3 }} />}
              {isSelf && (
                <label style={styles.avatarBtn} title="Change profile photo">
                  {uploading === 'photoURL' ? `${uploadPct}%` : '📷'}
                  <input type="file" accept="image/*" hidden disabled={!!uploading} onChange={(e) => handleMediaChange('photoURL', e)} />
                </label>
              )}
            </div>
            <div style={styles.actions}>
              {isSelf ? (
                <>
                  <button style={styles.primaryBtn} onClick={openEditor}>Edit profile</button>
                  <button style={styles.secondaryBtn} onClick={() => navigate('/settings')}>Settings</button>
                  {isStaff && <button style={styles.secondaryBtn} onClick={() => navigate('/studio')}>Admin</button>}
                </>
              ) : (
                <>
                  {!blocked && (
                    <button style={following ? styles.secondaryBtn : styles.primaryBtn} onClick={handleFollow} disabled={followBusy}>
                      {following ? 'Following' : 'Follow'}
                    </button>
                  )}
                  {!blocked && friend.status === 'none' && (
                    <button style={styles.primaryBtn} disabled={friendBusy} onClick={() => runFriendAction(() => sendFriendRequest(profile.uid))}>Add friend</button>
                  )}
                  {!blocked && friend.status === 'outgoing' && (
                    <button style={styles.secondaryBtn} disabled={friendBusy} title="Cancel friend request" onClick={() => runFriendAction(() => removeFriend(profile.uid), 'Cancel your friend request?')}>Request sent ✕</button>
                  )}
                  {!blocked && friend.status === 'incoming' && friend.requestId && (
                    <>
                      <button style={styles.primaryBtn} disabled={friendBusy} onClick={() => runFriendAction(() => acceptFriendRequest(friend.requestId as string))}>Accept request</button>
                      <button style={styles.secondaryBtn} disabled={friendBusy} onClick={() => runFriendAction(() => declineFriendRequest(friend.requestId as string))}>Decline</button>
                    </>
                  )}
                  {!blocked && friend.status === 'friends' && (
                    <button style={styles.secondaryBtn} disabled={friendBusy} title="Unfriend" onClick={() => runFriendAction(() => removeFriend(profile.uid), `Remove ${profile.name} from your friends?`)}>Friends ✓</button>
                  )}
                  {!blocked && (
                    <button style={styles.secondaryBtn} onClick={handleMessage}>Message</button>
                  )}
                  <button style={styles.dangerBtn} onClick={handleBlockToggle}>{blocked ? 'Unblock' : 'Block'}</button>
                </>
              )}
            </div>
            <h1 style={styles.name}>
              {profile.name}
              {profile.verified && <VerifiedBadge size={20} style={{ marginLeft: 6 }} />}
            </h1>
            {profile.username && <div style={styles.muted}>@{profile.username}</div>}
            {!isSelf && <PeerStatus userId={profile.uid} style={{ display: 'block', marginTop: 2 }} />}
            {profile.bio && <p style={styles.bio}>{renderTextWithLinks(profile.bio)}</p>}
            <div style={styles.metaRow}>
              {profile.location && (isSelf || privacy.showLocation !== false) && <span>📍 {profile.location}</span>}
              {websiteHref && <a href={websiteHref} target="_blank" rel="noopener noreferrer" style={styles.link}>🔗 {profile.website}</a>}
            </div>
            <div style={styles.stats}>
              {isSelf && (
                <button
                  style={{ ...styles.statBtn, ...(tab === 'friends' ? { color: 'var(--color-primary-700, #0B6B6F)', textDecoration: 'underline' } : null) }}
                  onClick={() => setTab('friends')}
                >
                  <strong>{friendCount ?? 0}</strong> friends
                </button>
              )}
              <button style={styles.statBtn} onClick={() => navigate(`${connectionsBase}?tab=followers`)}>
                <strong>{profile.followers || 0}</strong> followers
              </button>
              <button style={styles.statBtn} onClick={() => navigate(`${connectionsBase}?tab=following`)}>
                <strong>{profile.following || 0}</strong> following
              </button>
            </div>
            {blocked && <p style={styles.warning}>You have blocked this user.</p>}
          </div>
          <div style={styles.tabs}>
            {(['posts', 'pulse', 'live', 'about'] as Tab[]).map((t) => (
              <button key={t} style={tab === t ? styles.tabActive : styles.tab} onClick={() => setTab(t)}>
                {t === 'posts' ? `Posts (${posts.length})` : t === 'pulse' ? `Pulse (${pulses.length})` : t === 'live' ? `Live (${streams.length})` : t === 'friends' ? `Friends${friendCount !== null ? ` (${friendCount})` : ''}` : 'About'}
              </button>
            ))}
          </div>
        </div>

        {tab === 'posts' && (
          posts.length === 0 ? <div style={styles.card}><p style={styles.muted}>No posts yet.</p></div> : posts.map((p) => (
            <div key={p.id} style={styles.card}>
              <div style={styles.postHeader}>
                <span style={styles.muted}>{new Date(p.createdAt).toLocaleString()}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={styles.linkBtn} onClick={() => navigate(`/post/${encodeURIComponent(p.id)}`)}>Open</button>
                  {isSelf && <button style={{ ...styles.linkBtn, color: '#c0392b' }} onClick={() => handleDeletePost(p.id)}>Delete</button>}
                </div>
              </div>
              {p.type === 'repost' && <div style={styles.muted}>🔁 Shared a post</div>}
              {p.content && <p style={styles.postText}>{renderTextWithLinks(p.content)}</p>}
              {hasMultiMedia(p) ? (
                <PostMediaGallery media={p.media || []} poster={(p as any).thumbnailURL} postId={p.id} maxHeight={480} />
              ) : (
                <>
                  {p.imageURL && <MediaBox src={p.imageURL} type="image" maxHeight={480} />}
                  {p.videoURL && <MediaBox src={p.videoURL} type="video" controls maxHeight={480} />}
                </>
              )}
              <div style={styles.postFooter}>❤️ {p.likeCount || 0} · 💬 {p.comments || 0}</div>
            </div>
          ))
        )}

        {tab === 'pulse' && (
          pulses.length === 0 ? <div style={styles.card}><p style={styles.muted}>No Pulse videos yet.</p></div> : (
            <div style={styles.grid}>
              {pulses.map((v) => (
                <button key={v.id} style={styles.gridItem} onClick={() => navigate(`/pulse?videoId=${encodeURIComponent(v.id)}`)}>
                  {v.thumbnailURL ? (
                    <ImageWithFallback src={v.thumbnailURL} alt={v.caption} style={styles.gridMedia} />
                  ) : (
                    <MediaBox src={v.videoURL} type="video" muted maxHeight={260} />
                  )}
                  <div style={styles.gridCaption}>{v.caption || 'Pulse'} · 👁 {v.views || 0}</div>
                </button>
              ))}
            </div>
          )
        )}

        {tab === 'live' && (
          streams.length === 0 ? <div style={styles.card}><p style={styles.muted}>No live streams yet.</p></div> : streams.map((s) => (
            <div key={s.id} style={{ ...styles.card, cursor: 'pointer' }} onClick={() => navigate(s.isLive ? `/live/${encodeURIComponent(s.id)}` : `/live/rewatch/${encodeURIComponent(s.id)}`)}>
              <div style={styles.postHeader}>
                <strong>{s.title || 'Live stream'}</strong>
                {s.isLive ? <span style={styles.liveBadge}>LIVE</span> : <span style={styles.muted}>Ended</span>}
              </div>
              {s.description && <p style={styles.postText}>{s.description}</p>}
              <span style={styles.muted}>{s.createdAt ? new Date(s.createdAt).toLocaleString() : ''}</span>
              {isSelf && !s.isLive && (
                <button style={{ ...styles.linkBtn, color: '#c0392b', marginLeft: 12 }} onClick={(e) => { e.stopPropagation(); handleDeleteStream(s.id); }}>Delete</button>
              )}
            </div>
          ))
        )}

        {tab === 'friends' && isSelf && <FriendsList onCount={setFriendCount} />}

        {tab === 'about' && (
          <div style={styles.card}>
            <h3 style={{ marginTop: 0 }}>About {profile.name}</h3>
            {profile.bio ? <p style={styles.postText}>{profile.bio}</p> : <p style={styles.muted}>No bio yet.</p>}
            {profile.location && (isSelf || privacy.showLocation !== false) && <p>📍 {profile.location}</p>}
            {age !== null && (isSelf || privacy.showAge === true) && <p>🎂 {age} years old</p>}
            {profile.gender && (isSelf || privacy.showGender === true) && <p>👤 {profile.gender.replace(/_/g, ' ')}</p>}
            {(profile.interests || []).length > 0 && (
              <div style={styles.chips}>
                {(profile.interests || []).map((i) => <span key={i} style={styles.chip}>{i}</span>)}
              </div>
            )}
          </div>
        )}
      </div>

      {editing && form && (
        <div style={styles.backdrop} onClick={() => !saving && setEditing(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Edit profile</h2>
            <label style={styles.label}>Name
              <input style={styles.input} value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label style={styles.label}>Username
              <input style={styles.input} value={form.username} maxLength={40} placeholder="yourname" onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </label>
            <label style={styles.label}>Bio
              <textarea style={{ ...styles.input, minHeight: 80 }} value={form.bio} maxLength={1000} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            </label>
            <label style={styles.label}>Location
              <input style={styles.input} value={form.location} maxLength={120} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </label>
            <label style={styles.label}>Website
              <input style={styles.input} value={form.website} maxLength={240} placeholder="https://" onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </label>
            <label style={styles.label}>Interests (comma separated)
              <input style={styles.input} value={form.interests} placeholder="Worship, Prayer, Bible study" onChange={(e) => setForm({ ...form, interests: e.target.value })} />
            </label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ ...styles.label, flex: 1, minWidth: 160 }}>Date of birth
                <input type="date" style={styles.input} value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
              </label>
              <label style={{ ...styles.label, flex: 1, minWidth: 160 }}>Gender
                <select style={styles.input} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
            </div>
            <div style={styles.checks}>
              <label><input type="checkbox" checked={form.showLocation} onChange={(e) => setForm({ ...form, showLocation: e.target.checked })} /> Show location</label>
              <label><input type="checkbox" checked={form.showAge} onChange={(e) => setForm({ ...form, showAge: e.target.checked })} /> Show age</label>
              <label><input type="checkbox" checked={form.showGender} onChange={(e) => setForm({ ...form, showGender: e.target.checked })} /> Show gender</label>
            </div>
            {error && <p style={styles.error}>{error}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.secondaryBtn} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button style={styles.primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: 'var(--color-background-alt)' },
  content: { paddingTop: 76, marginLeft: 300, marginRight: 340, paddingLeft: 20, paddingRight: 20, paddingBottom: 40, maxWidth: 760 },
  contentMobile: { paddingTop: 12, paddingLeft: 12, paddingRight: 12, paddingBottom: 88 },
  card: { background: 'var(--color-surface, #fff)', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  headerCard: { background: 'var(--color-surface, #fff)', borderRadius: 12, marginBottom: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  cover: { position: 'relative', height: 200, background: 'linear-gradient(135deg, #0F6C6E, #66EAD1)' },
  coverImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  coverBtn: { position: 'absolute', right: 12, bottom: 12, background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  headerBody: { position: 'relative', display: 'flow-root', padding: '0 20px 16px' },
  avatarWrap: { position: 'relative', width: 120, height: 120, marginTop: -60 },
  avatar: { width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: '4px solid var(--color-surface, #fff)', background: '#e8f7f4' },
  avatarBtn: { position: 'absolute', right: 0, bottom: 4, background: '#0F6C6E', color: '#fff', borderRadius: '50%', minWidth: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13, padding: '0 4px' },
  actions: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 },
  name: { margin: '10px 0 2px', fontSize: 'clamp(19px, 5vw, 24px)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, wordBreak: 'break-word', color: 'var(--color-text, #072B2C)' },
  bio: { margin: '10px 0', whiteSpace: 'pre-wrap', color: 'var(--color-text, #072B2C)' },
  metaRow: { display: 'flex', gap: 16, flexWrap: 'wrap', color: '#5B6E6E', fontSize: 14 },
  stats: { display: 'flex', gap: 18, marginTop: 12, alignItems: 'center', color: 'var(--color-text, #072B2C)' },
  statBtn: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', fontSize: 'inherit' },
  tabs: { display: 'flex', borderTop: '1px solid rgba(0,0,0,0.08)', overflowX: 'auto' },
  tab: { flex: 1, padding: '12px 8px', background: 'none', border: 'none', borderBottom: '3px solid transparent', cursor: 'pointer', color: '#5B6E6E', fontWeight: 600, whiteSpace: 'nowrap' },
  tabActive: { flex: 1, padding: '12px 8px', background: 'none', border: 'none', borderBottom: '3px solid #0F6C6E', cursor: 'pointer', color: '#0F6C6E', fontWeight: 700, whiteSpace: 'nowrap' },
  primaryBtn: { background: '#0F6C6E', color: '#fff', border: '1px solid #0F6C6E', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  secondaryBtn: { background: '#E6F7F4', color: '#0F6C6E', border: '1px solid #9fded3', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  dangerBtn: { background: 'transparent', color: '#c0392b', border: '1px solid #e5b4ae', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', color: '#0F6C6E', cursor: 'pointer', fontWeight: 600, padding: 0 },
  link: { color: '#0F6C6E' },
  muted: { color: '#5B6E6E', fontSize: 14 },
  warning: { color: '#c0392b', marginTop: 10 },
  postHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  postText: { whiteSpace: 'pre-wrap', margin: '6px 0 10px', color: 'var(--color-text, #072B2C)' },
  postFooter: { marginTop: 8, color: '#5B6E6E', fontSize: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 },
  gridItem: { background: '#000', border: 'none', borderRadius: 12, overflow: 'hidden', padding: 0, cursor: 'pointer', textAlign: 'left' },
  gridMedia: { width: '100%', height: 260, objectFit: 'cover', display: 'block' },
  gridCaption: { color: '#fff', padding: '8px 10px', fontSize: 13, background: '#072B2C' },
  liveBadge: { background: '#e53935', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { background: '#E6F7F4', color: '#0F6C6E', borderRadius: 999, padding: '4px 10px', fontSize: 13 },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 12 },
  modal: { background: 'var(--color-surface, #fff)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' },
  label: { display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 10, color: 'var(--color-text, #072B2C)' },
  input: { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #cfd8d8', fontSize: 14, fontFamily: 'inherit' },
  checks: { display: 'flex', gap: 16, flexWrap: 'wrap', margin: '6px 0 12px', fontSize: 14 },
  error: { color: '#c0392b', fontSize: 14 },
};

export default Profile;
